import { useState, useEffect } from 'react';
import { FolderKanban, Check, ChevronDown, Plus, Settings, Star, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProject } from '../contexts/ProjectContext';
import { useSubscription } from '../hooks/useSubscription';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useNavigate } from 'react-router-dom';

const ProjectSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currentProject,
    allProjects,
    selectedProjectId,
    setSelectedProjectId,
    isLoadingProjects,
    canCreateProject,
    projectLimits,
    projectAddons,
    setShowAddonModal
  } = useProject();
  const { subscription } = useSubscription();
  const { isImpersonating } = useImpersonation();
  const isPremiumPlan = subscription?.subscription_plan === 'premium';
  const pricePerSlot = projectAddons?.price_per_slot ?? 5;
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.project-selector')) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  if (isLoadingProjects) {
    return null;
  }

  const hasProjects = allProjects.length > 0;
  const displayName = currentProject?.name || t('project_selector.no_projects');

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    setShowMenu(false);
  };

  const handleCreateProject = () => {
    setShowMenu(false);
    navigate('/projects/new');
  };

  const handleManageProjects = () => {
    setShowMenu(false);
    navigate('/projects');
  };

  const projectsUsed = projectLimits?.projects.current ?? 0;
  const projectsMax = projectLimits?.projects.max ?? 1;
  const isUnlimited = projectLimits?.projects.unlimited ?? false;

  return (
    <div className="relative project-selector">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`flex items-center space-x-2 px-3 py-2 transition-colors rounded-lg ${
          isImpersonating
            ? 'text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 border border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50'
            : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
        }`}
      >
        <FolderKanban className="h-4 w-4" />
        <span className="text-sm font-medium hidden sm:inline max-w-32 truncate">{displayName}</span>
        {currentProject?.is_default && (
          <Star className="h-3 w-3 text-amber-500 hidden sm:inline" />
        )}
        <ChevronDown className="h-4 w-4" />
      </button>

      {showMenu && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 project-menu">
          <div className="p-2">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700 mb-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('project_selector.projects')}
              </span>
              {!isUnlimited && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {projectsUsed} / {projectsMax} {t('project_selector.used')}
                </span>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto">
              {hasProjects ? (
                allProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleProjectSelect(project.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                      selectedProjectId === project.id
                        ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FolderKanban className="h-4 w-4 mr-3 flex-shrink-0" />
                    <span className="truncate flex-1 text-left">{project.name}</span>
                    {project.is_default && (
                      <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 px-2 py-0.5 rounded-full ml-2 flex-shrink-0">
                        Default
                      </span>
                    )}
                    {selectedProjectId === project.id && (
                      <Check className="ml-2 h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-center">
                  <FolderKanban className="h-8 w-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('project_selector.no_projects_yet')}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('project_selector.create_first')}</p>
                </div>
              )}
            </div>

            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-1">
              <button
                onClick={handleCreateProject}
                disabled={!canCreateProject}
                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                  canCreateProject
                    ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                    : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
                title={!canCreateProject ? 'Project limit reached. Upgrade your plan to create more projects.' : undefined}
              >
                <Plus className="h-4 w-4 mr-3" />
                <span>{t('project_selector.create_new')}</span>
                {!canCreateProject && (
                  <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{t('project_selector.limit_reached')}</span>
                )}
              </button>

              {!canCreateProject && (
                isPremiumPlan ? (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowAddonModal(true);
                    }}
                    className="w-full flex items-center px-3 py-2 text-sm rounded-lg text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-3" />
                    <span>{t('project_selector.add_slot', 'Add slot (+${{price}}/mo)', { price: pricePerSlot })}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/settings?tab=billing');
                    }}
                    className="w-full flex items-center px-3 py-2 text-sm rounded-lg text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
                  >
                    <Zap className="h-4 w-4 mr-3" />
                    <span>{t('project_selector.upgrade_plan', 'Upgrade plan')}</span>
                  </button>
                )
              )}

              <button
                onClick={handleManageProjects}
                className="w-full flex items-center px-3 py-2 text-sm rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Settings className="h-4 w-4 mr-3" />
                <span>{t('project_selector.manage_projects')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelector;
